// The date-range picker in the pull modal.
//
// Hand-built rather than two <input type="date">, and the reason is the badges. An
// admin about to pull needs to see which days already have data — that is the whole
// point of choosing a range rather than accepting the pipeline's default window — and
// the native picker's popup is user-agent shadow DOM. Nothing can be drawn inside it.
//
// Owning the control means owning what the browser was doing for us: keyboard
// navigation, focus management, and an accessible name for every cell. That is most of
// the code below, and it is not optional — this is the only way to choose the dates.
//
// The grid follows the WAI-ARIA date-picker pattern: role="grid", one tab stop for the
// whole calendar, arrows to move within it. Tabbing through 31 buttons to reach the
// end of a month is the failure mode this avoids.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type DateCoverage } from "./payflowApi";
import { ymdFromISO } from "./payflowStore";
import {
  addMonthsISO,
  daysInMonth,
  firstOfMonthISO,
  inclusiveSpanDays,
  isoAddDays,
  monthKeyOf,
  weekdayOfISO,
} from "./payflowDates";

const WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];
const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const DAYS_PER_WEEK = 7;

// What a badge means, in words. These reach a screen reader, so they carry the whole
// meaning — a sighted user gets the same from the legend, and neither depends on
// telling two shades of green apart.
const COVERAGE_LABELS: Record<DateCoverage["state"], string> = {
  full: "data already pulled",
  partial: "partly pulled — one source is missing",
  none: "no data yet",
};

const monthLabel = (iso: string): string => {
  const { year, monthIndex, day } = ymdFromISO(iso);
  return new Date(year, monthIndex, day).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const dayLabel = (iso: string): string => {
  const { year, monthIndex, day } = ymdFromISO(iso);
  return new Date(year, monthIndex, day).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

// The dates drawn in one month's grid: the month, padded at the front to the Sunday
// before it and at the back to fill the final week. Padding days are real dates from
// the neighbouring months and are selectable — a fortnight that straddles a month
// boundary is the normal case here, so making them dead would be actively unhelpful.
const gridDatesFor = (monthISO: string): string[] => {
  const first = firstOfMonthISO(monthISO);
  const start = isoAddDays(first, -weekdayOfISO(first));
  const last = isoAddDays(first, daysInMonth(first) - 1);
  const end = isoAddDays(last, DAYS_PER_WEEK - 1 - weekdayOfISO(last));
  return Array.from({ length: inclusiveSpanDays(start, end) }, (_, i) => isoAddDays(start, i));
};

export interface PullRangeCalendarProps {
  startISO: string; // selected range start, "" when nothing is chosen
  endISO: string; // selected range end, "" while a start is pending
  onChange: (startISO: string, endISO: string) => void;
  maxISO: string; // the newest selectable date (the last closed pay period's end)
  maxSpanDays: number; // longest range that may be selected
  coverage: Record<string, DateCoverage>;
  loadedMonths: readonly string[]; // month keys whose coverage has been asked for
  onMonthVisible: (monthKey: string, startISO: string, endISO: string) => void;
}

export default function PullRangeCalendar({
  startISO,
  endISO,
  onChange,
  maxISO,
  maxSpanDays,
  coverage,
  loadedMonths,
  onMonthVisible,
}: PullRangeCalendarProps) {
  // Which month is on screen. Seeded from the selection so the calendar opens showing
  // the dates that are already chosen rather than today's month.
  const [monthISO, setMonthISO] = useState(() => firstOfMonthISO(startISO || maxISO));
  // The date the arrow keys are on. Separate from the selection: moving through a
  // calendar is not choosing, and conflating them would select a date per keystroke.
  const [focusISO, setFocusISO] = useState(() => startISO || maxISO);
  // Set on the first click of a new range, cleared on the second. While it is set the
  // grid previews the range under the pointer.
  const [pendingStart, setPendingStart] = useState<string | null>(null);
  const [hoverISO, setHoverISO] = useState<string | null>(null);
  // Whether focus should be moved to the focused cell after this render. Only true
  // when a keystroke moved it — otherwise opening the modal would steal focus from
  // wherever it belongs, and a click would yank it back mid-gesture.
  const shouldRestoreFocus = useRef(false);
  const gridRef = useRef<HTMLDivElement | null>(null);

  const dates = useMemo(() => gridDatesFor(monthISO), [monthISO]);
  const monthKey = monthKeyOf(monthISO);

  // Ask for this month's badges once it is on screen. The grid spills into the
  // neighbouring months, so the request covers the whole visible span rather than the
  // calendar month — otherwise the padding days would never get a badge.
  useEffect(() => {
    if (!dates.length) return;
    onMonthVisible(monthKey, dates[0], dates[dates.length - 1]);
  }, [monthKey, dates, onMonthVisible]);

  useEffect(() => {
    if (!shouldRestoreFocus.current) return;
    shouldRestoreFocus.current = false;
    const cell = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-date="${focusISO}"]`,
    );
    cell?.focus();
  }, [focusISO]);

  const isSelectable = useCallback((iso: string) => iso <= maxISO, [maxISO]);

  // The range currently drawn: the committed selection, or the one being dragged out
  // from a pending start to whatever the pointer or keyboard is on.
  const preview = useMemo(() => {
    if (pendingStart) {
      const other = hoverISO ?? focusISO;
      const [from, to] = other < pendingStart ? [other, pendingStart] : [pendingStart, other];
      // Only preview a range that could actually be selected, so an over-long drag
      // does not paint a fortnight the click will refuse.
      if (inclusiveSpanDays(from, to) <= maxSpanDays && to <= maxISO) {
        return { from, to };
      }
      return { from: pendingStart, to: pendingStart };
    }
    if (startISO && endISO) return { from: startISO, to: endISO };
    return null;
  }, [pendingStart, hoverISO, focusISO, startISO, endISO, maxSpanDays, maxISO]);

  /**
   * One click on a day.
   *
   * Never dead-ends. A click that cannot complete the range in progress — before the
   * pending start, or further from it than the limit allows — starts a new range at
   * that date instead of being ignored. Silently doing nothing is the behaviour people
   * read as a broken control, and "start again from here" is what they meant anyway.
   */
  const pick = useCallback(
    (iso: string) => {
      if (!isSelectable(iso)) return;
      setFocusISO(iso);

      if (pendingStart && iso >= pendingStart && inclusiveSpanDays(pendingStart, iso) <= maxSpanDays) {
        setPendingStart(null);
        setHoverISO(null);
        onChange(pendingStart, iso);
        return;
      }
      setPendingStart(iso);
      setHoverISO(null);
      // The range is incomplete until the second click, and the caller is told so with
      // an empty end rather than a one-day range — otherwise pressing "Pull now"
      // mid-gesture would pull a single day nobody asked for.
      onChange(iso, "");
    },
    [isSelectable, pendingStart, maxSpanDays, onChange],
  );

  // Move the keyboard position, and take the visible month with it. Clamped at the
  // ceiling so arrowing forward stops at the newest selectable date instead of walking
  // off into months that hold nothing selectable.
  const focusOn = useCallback(
    (iso: string, restoreFocus: boolean) => {
      const next = iso > maxISO ? maxISO : iso;
      shouldRestoreFocus.current = restoreFocus;
      setFocusISO(next);
      // Following the focus is what makes arrowing off the edge of a month scroll the
      // calendar rather than move focus somewhere invisible.
      setMonthISO((current: string) =>
        monthKeyOf(next) === monthKeyOf(current) ? current : firstOfMonthISO(next),
      );
    },
    [maxISO],
  );

  const moveFocus = useCallback(
    (days: number) => focusOn(isoAddDays(focusISO, days), true),
    [focusOn, focusISO],
  );

  // Whole months. `restoreFocus` is what separates the two callers: PageUp/PageDown
  // must carry the focus ring with them, and the ‹ › buttons must not — pulling focus
  // into the grid would take it off the button being clicked, so a second click would
  // land on nothing.
  const jumpMonths = useCallback(
    (delta: number, restoreFocus: boolean) => focusOn(addMonthsISO(focusISO, delta), restoreFocus),
    [focusOn, focusISO],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const weekday = weekdayOfISO(focusISO);
      const handlers: Record<string, () => void> = {
        ArrowLeft: () => moveFocus(-1),
        ArrowRight: () => moveFocus(1),
        ArrowUp: () => moveFocus(-DAYS_PER_WEEK),
        ArrowDown: () => moveFocus(DAYS_PER_WEEK),
        Home: () => moveFocus(-weekday),
        End: () => moveFocus(DAYS_PER_WEEK - 1 - weekday),
        PageUp: () => jumpMonths(-1, true),
        PageDown: () => jumpMonths(1, true),
        Escape: () => {
          // Abandon a half-made range rather than leaving the grid painted with a
          // preview that no longer follows anything.
          if (!pendingStart) return;
          setPendingStart(null);
          setHoverISO(null);
        },
      };
      const handler = handlers[event.key];
      if (!handler) return;
      event.preventDefault();
      handler();
    },
    [moveFocus, jumpMonths, focusISO, pendingStart],
  );

  // Forward navigation stops at the month holding the ceiling: there is nothing
  // selectable beyond it, and letting someone page into empty months is a dead end.
  const canGoForward = monthKeyOf(monthISO) < monthKeyOf(maxISO);
  const monthLoaded = loadedMonths.includes(monthKey);

  const weeks = useMemo(() => {
    const out: string[][] = [];
    for (let i = 0; i < dates.length; i += DAYS_PER_WEEK) {
      out.push(dates.slice(i, i + DAYS_PER_WEEK));
    }
    return out;
  }, [dates]);

  return (
    <div className="payflow-cal">
      <div className="payflow-cal-head">
        <button
          type="button"
          className="payflow-cal-nav"
          onClick={() => jumpMonths(-1, false)}
          aria-label="Previous month"
        >
          ‹
        </button>
        {/* aria-live so a screen reader hears the month change; the grid itself is
            relabelled by it, but a silent swap under the cursor is disorienting. */}
        <div className="payflow-cal-month" aria-live="polite">
          {monthLabel(monthISO)}
        </div>
        <button
          type="button"
          className="payflow-cal-nav"
          onClick={() => jumpMonths(1, false)}
          disabled={!canGoForward}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div
        className="payflow-cal-grid"
        role="grid"
        aria-label={`Choose the dates to pull — ${monthLabel(monthISO)}`}
        onKeyDown={onKeyDown}
        onMouseLeave={() => setHoverISO(null)}
        ref={gridRef}
      >
        <div className="payflow-cal-row payflow-cal-weekdays" role="row">
          {WEEKDAY_INITIALS.map((initial, i) => (
            <div key={WEEKDAY_NAMES[i]} className="payflow-cal-weekday" role="columnheader">
              {/* The initial is shown; the full name is what gets announced. Four of
                  the seven initials are ambiguous on their own. */}
              <span aria-hidden="true">{initial}</span>
              <span className="payflow-sr-only">{WEEKDAY_NAMES[i]}</span>
            </div>
          ))}
        </div>

        {weeks.map((week) => (
          <div className="payflow-cal-row" role="row" key={week[0]}>
            {week.map((iso) => {
              const outsideMonth = monthKeyOf(iso) !== monthKey;
              const unavailable = !isSelectable(iso);
              const inRange = !!preview && iso >= preview.from && iso <= preview.to;
              const isFrom = preview?.from === iso;
              const isTo = preview?.to === iso;
              // A month whose badges have not arrived shows none at all. Drawing
              // "no data" for an unanswered question would tell an admin their data
              // is missing when nobody has looked yet.
              const state = monthLoaded ? coverage[iso]?.state ?? "none" : undefined;

              const classes = [
                "payflow-cal-day",
                outsideMonth ? "payflow-cal-day-outside" : "",
                unavailable ? "payflow-cal-day-unavailable" : "",
                inRange ? "payflow-cal-day-inrange" : "",
                isFrom ? "payflow-cal-day-from" : "",
                isTo ? "payflow-cal-day-to" : "",
                state ? `payflow-cal-day-${state}` : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <button
                  type="button"
                  key={iso}
                  data-date={iso}
                  role="gridcell"
                  className={classes}
                  // One tab stop for the whole grid — the WAI-ARIA grid pattern. Every
                  // other cell is reachable with the arrow keys.
                  tabIndex={iso === focusISO ? 0 : -1}
                  // aria-disabled rather than `disabled`. A disabled button cannot take
                  // focus, so arrowing into the days past the ceiling would strand the
                  // keyboard on a cell that refuses to receive it. This way the cell is
                  // still reachable and announced as unavailable, and `pick` is what
                  // actually refuses.
                  aria-disabled={unavailable || undefined}
                  aria-selected={inRange || undefined}
                  aria-label={
                    `${dayLabel(iso)}` +
                    `${unavailable ? " — not available yet" : ""}` +
                    `${state ? ` — ${COVERAGE_LABELS[state]}` : ""}`
                  }
                  onClick={() => pick(iso)}
                  onFocus={() => setFocusISO(iso)}
                  onMouseEnter={() => !unavailable && setHoverISO(iso)}
                >
                  <span className="payflow-cal-daynum">{Number(iso.slice(8, 10))}</span>
                  {/* Decorative: the state is already in the button's accessible name,
                      so announcing it again would read every date twice. */}
                  {state && state !== "none" && (
                    <span className="payflow-cal-badge" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="payflow-cal-legend">
        <span className="payflow-cal-legend-item">
          <span className="payflow-cal-swatch payflow-cal-day-full" aria-hidden="true" />
          Already pulled
        </span>
        <span className="payflow-cal-legend-item">
          <span className="payflow-cal-swatch payflow-cal-day-partial" aria-hidden="true" />
          Partly pulled
        </span>
        <span className="payflow-cal-legend-item">
          <span className="payflow-cal-swatch" aria-hidden="true" />
          No data yet
        </span>
      </div>
    </div>
  );
}
